import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "components/ui/tooltip";
import {
  isNavItemActive,
  navlinks,
  NavLinkItem,
  privateNavlinks,
} from "../../shared/utils/navRoutes";
import auth from "shared/Auth0";
import { UseLivlyLeaseUser } from "shared/api/LivlyUserProvider";
import { isAdmin } from "shared/api/IsAdmin";
import { isSuperUser } from "shared/api/isSuperUser";
import { useSidebarState } from "shared/context/SidebarState";
import TenantSwitcher from "../Tenants/TenantSwitcher";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";

function cn(...classes: (string | boolean | undefined | null)[]) {
  return classes.filter(Boolean).join(" ");
}

const NAVIGATION_ICON_SIZE = 18;

function NavItem({
  item,
  collapsed,
}: {
  item: NavLinkItem;
  collapsed: boolean;
}) {
  const { pathname } = useLocation();
  const isActive = isNavItemActive(pathname, item);

  const navigationLink = (
    <Link
      to={item.link}
      aria-current={isActive ? "page" : undefined}
      className={cn(
        isActive
          ? "bg-blue-50 text-blue-700"
          : "text-gray-700 hover:bg-blue-50 hover:text-blue-700",
        collapsed ? "justify-center" : "gap-x-3",
        "group flex rounded-md p-2 text-sm/6 font-semibold",
      )}
    >
      <FontAwesomeIcon
        icon={item.icon as IconProp}
        aria-hidden="true"
        style={{ fontSize: NAVIGATION_ICON_SIZE }}
        className={cn(
          isActive
            ? "text-blue-700"
            : "text-gray-400 group-hover:text-blue-700",
          "shrink-0 self-center",
        )}
      />
      {!collapsed && <span className="truncate">{item.name}</span>}
    </Link>
  );

  if (!collapsed) {
    return navigationLink;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{navigationLink}</TooltipTrigger>
      <TooltipContent side="right">{item.name}</TooltipContent>
    </Tooltip>
  );
}

const SidebarTailwind = () => {
  const user = UseLivlyLeaseUser();
  const isSuperUserPermission = isSuperUser(user);
  const { collapsed, toggle } = useSidebarState();

  const privateNavs = privateNavlinks().filter(
    (link) => !link.requiresSuperUser || isSuperUserPermission,
  );
  const visibleNavlinks = navlinks.filter(
    (link) => !link.requiresSuperUser || isSuperUserPermission,
  );
  const homeLink = visibleNavlinks.find((link) => link.name === "Home");
  const otherNavlinks = visibleNavlinks.filter((link) => link.name !== "Home");

  return (
    <div
      className={cn(
        "hidden md:fixed md:inset-y-0 md:z-50 md:flex md:flex-col transition-all duration-300",
        collapsed ? "md:w-16" : "md:w-64",
      )}
    >
      <div
        className={cn(
          "flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white transition-all duration-300",
          collapsed ? "px-3" : "px-6",
        )}
      >
        {/* Header */}
        <div className="flex items-center h-16 overflow-hidden shrink-0">
          {!collapsed && (
            <Link to="/admin" className="flex-1 min-w-0">
              <strong className="text-gray-900 whitespace-nowrap">
                LivlyConnect
              </strong>
            </Link>
          )}
          <button
            onClick={toggle}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={cn(
              "rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600",
              collapsed ? "mx-auto" : "ml-auto",
            )}
          >
            <FontAwesomeIcon
              icon={(collapsed ? "chevron-right" : "chevron-left") as IconProp}
              className="w-4 h-4"
            />
          </button>
        </div>

        {/* Tenant picker — icon-only trigger when collapsed */}
        {collapsed ? (
          <div className="flex justify-center">
            <TenantSwitcher iconOnly />
          </div>
        ) : (
          <div className="flex items-center text-sm text-gray-700 gap-x-2">
            <TenantSwitcher compact />
          </div>
        )}

        <nav className="flex flex-col flex-1">
          <ul role="list" className="flex flex-col flex-1 gap-y-7">
            {/* Home, pinned above the Admin subheader */}
            {homeLink && (
              <li className={cn(!collapsed && "-mx-2")}>
                <NavItem item={homeLink} collapsed={collapsed} />
              </li>
            )}

            {/* Admin section (role-gated) */}
            {isAdmin(user) && (
              <li>
                {!collapsed && (
                  <div className="font-semibold text-gray-400 text-xs/6">
                    Admin
                  </div>
                )}
                <ul
                  role="list"
                  className={cn(!collapsed && "-mx-2 mt-2", "space-y-1")}
                >
                  {privateNavs.map((item) => (
                    <li key={item.name}>
                      <NavItem item={item} collapsed={collapsed} />
                    </li>
                  ))}
                </ul>
              </li>
            )}

            {/* Remaining nav links */}
            <li>
              <ul
                role="list"
                className={cn(!collapsed && "-mx-2", "space-y-1")}
              >
                {otherNavlinks.map((item) => (
                  <li key={item.name}>
                    <NavItem item={item} collapsed={collapsed} />
                  </li>
                ))}
              </ul>
            </li>

            {/* User profile + logout, pinned to bottom */}
            <li
              className={cn(
                collapsed ? "-mx-3" : "-mx-6",
                "mt-auto border-t border-gray-200",
              )}
            >
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                  className={cn(
                    "flex w-full items-center py-3 text-sm/6 font-semibold text-gray-900 hover:bg-gray-50",
                    collapsed ? "justify-center px-0" : "gap-x-3 px-6",
                  )}
                >
                  <span className="flex items-center justify-center text-xs font-semibold text-blue-700 bg-blue-100 rounded-full size-8 shrink-0">
                    {user?.name?.charAt(0).toUpperCase() || (
                      <FontAwesomeIcon
                        icon={"user" as IconProp}
                        className="size-4"
                      />
                    )}
                  </span>
                  {!collapsed && (
                    <span className="flex-1 min-w-0 text-left">
                      <span className="block text-sm font-semibold text-gray-900 truncate">
                        {user?.name || "Account"}
                      </span>
                      {user?.email && (
                        <span className="block text-xs font-normal text-gray-500 truncate">
                          {user.email}
                        </span>
                      )}
                    </span>
                  )}
                  </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                  align="end"
                  className="w-48"
                  side="right"
                  sideOffset={8}
                >
                  <DropdownMenuItem
                    className="gap-x-2 text-gray-700"
                    onSelect={() => auth.logout()}
                  >
                    <FontAwesomeIcon
                      icon={"right-from-bracket" as IconProp}
                      aria-hidden="true"
                      className="size-4 text-gray-400"
                    />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );
};

export default SidebarTailwind;
